import { useInfiniteQuery } from "@tanstack/react-query";
import { getClaimsByCompanyIdInfiniteQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";
import { ClaimParams, ClaimsItemDto, DEFAULT_QUERY_FILTER } from "@/api/claimsApi";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { InvoiceFormData } from "@/types";
import { Box, Flex, Icon, Input, InputGroup, List, Text } from "@chakra-ui/react";
import { useCombobox } from "downshift";
import { CloseIcon, SearchIconSM } from "@digital-services-gard-as/anchor-icons";
import { TruncatedText } from "@/components/TruncatedText";
import { t } from "i18next";
import { Button } from "@digital-services-gard-as/anchor-react";
import { NoResults } from "@/components/NoResult";

const SEARCH_DEBOUNCE_MS = 1000;
const SCROLL_THRESHOLD_PX = 100;

type ClaimsSearchProps = {
    tempClaimRef?: string | null;
    onClaimChange: (claimRef: string, clientName: string, vesselName: string) => void;
};

export const ClaimsSearch = ({ tempClaimRef, onClaimChange }: ClaimsSearchProps) => {
    const { watch } = useFormContext<InvoiceFormData>();

    const claimIdRef = useRef<string>(tempClaimRef || watch("claimRef") || "");

    const [claimsQuery, setClaimsQuery] = useState<ClaimParams>({
        queryFilter: {
            ...DEFAULT_QUERY_FILTER,
            sortBy: "incidentDate",
            query: claimIdRef.current
        }
    });

    const {
        data: claimsQueryResult,
        isLoading: claimsQueryLoading,
        isFetchingNextPage: claimsQueryFetchingNextPage,
        hasNextPage: claimsQueryHasNextPage,
        fetchNextPage: claimsQueryFetchNextPage
    } = useInfiniteQuery(getClaimsByCompanyIdInfiniteQueryOptions(claimsQuery));

    const claimsList = useMemo(
        () => claimsQueryResult?.pages.flatMap(p => p.items ?? []) ?? undefined,
        [claimsQueryResult?.pages]
    );

    const handleClaimSelect = useCallback(
        (claimId: string) => {
            claimIdRef.current = claimId;

            const claim = claimsList?.find(c => c.id === claimId);
            if (!claim) {
                return;
            }

            onClaimChange(claimId, claim.clientName, claim.vesselName);
        },
        [claimsList, onClaimChange]
    );

    const handleOnSearch = useCallback((query: string) => {
        setClaimsQuery(prev => ({
            ...prev,
            queryFilter: {
                ...prev.queryFilter,
                query,
                page: DEFAULT_QUERY_FILTER.page
            }
        }));
    }, []);

    return (
        <ClaimsDropdownSearch
            claims={claimsList}
            initialInputValue={claimIdRef.current}
            isLoading={claimsQueryLoading || claimsQueryFetchingNextPage}
            hasMore={Boolean(claimsQueryHasNextPage)}
            loadMore={claimsQueryFetchNextPage}
            onSelect={handleClaimSelect}
            onSearch={handleOnSearch}
        />
    );
};

type ClaimsDropdownSearchProps = {
    claims: ClaimsItemDto[] | undefined;
    initialInputValue: string;
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
    onSearch: (value: string) => void;
    onSelect: (claimId: string) => void;
};

const ClaimsDropdownSearch = ({
    claims,
    isLoading,
    hasMore,
    loadMore,
    onSearch,
    onSelect,
    initialInputValue
}: ClaimsDropdownSearchProps) => {
    const listRef = useRef<HTMLDivElement>(null);

    const {
        isOpen,
        inputValue,
        highlightedIndex,
        getMenuProps,
        getItemProps,
        getInputProps,
        getToggleButtonProps,
        selectItem
    } = useCombobox({
        items: claims || [],
        itemToString: item => item?.id ?? "",
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem?.id) {
                onSelect(selectedItem.id);
            }
        },
        initialInputValue
    });

    /* Debounced search */
    useEffect(() => {
        const timer = setTimeout(() => {
            onSearch((inputValue ?? "").trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [inputValue, onSearch]);

    /* Infinite scroll */
    useEffect(() => {
        const el = listRef.current;

        if (!el || !isOpen) {
            return undefined;
        }

        const onScroll = () => {
            if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX && hasMore && !isLoading) {
                loadMore();
            }
        };

        el.addEventListener("scroll", onScroll);

        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, [isOpen, hasMore, isLoading, loadMore]);

    const handleOnClick = useCallback(() => {
        selectItem(null);
    }, [selectItem]);

    const showNoResults = [isOpen, Boolean(claims), claims?.length === 0, !isLoading].every(Boolean);

    return (
        <Flex w="100%" flexDir="column" alignItems="center" data-testid="claimsSearch">
            <InputGroup
                maxW="30rem"
                mb="0.5rem"
                startElement={
                    <Icon aria-label="Search claims">
                        <SearchIconSM />
                    </Icon>
                }
                endElement={
                    <>
                        {inputValue && (
                            <Button
                                {...getToggleButtonProps()}
                                aria-label="Clear"
                                variant="tertiary"
                                onClick={handleOnClick}
                                icon={<CloseIcon />}
                                style={{ fontSize: "1.5rem" }}
                            />
                        )}
                    </>
                }
            >
                <Input
                    {...getToggleButtonProps()}
                    data-testid="claimSearchInput"
                    border="1px solid"
                    borderColor="anchor.brown.500"
                    color="anchor.gray.600"
                    size="lg"
                    fontSize="15px"
                    placeholder={t("invoices:createInvoice.generalInfo.claimsSearchPlaceholder")}
                    {...getInputProps()}
                />
            </InputGroup>
            <Box position="relative" w="100%" maxW="30rem">
                <Flex
                    ref={listRef}
                    position="absolute"
                    w="100%"
                    maxH="30rem"
                    overflowY="auto"
                    bg="white"
                    border="1px solid"
                    borderColor="anchor.gray.200"
                    borderRadius="0.5rem"
                    zIndex={2}
                >
                    <List.Root {...getMenuProps()} w="100%">
                        {isOpen && (
                            <>
                                {showNoResults && (
                                    <List.Item p={4} textAlign="center">
                                        <NoResults />
                                    </List.Item>
                                )}

                                {claims?.map((claim, index) => (
                                    <List.Item
                                        key={claim.id}
                                        {...getItemProps({ item: claim, index })}
                                        data-testid="claimSearchResult"
                                        bg={highlightedIndex === index ? "anchor.gray.200" : "white"}
                                        cursor="pointer"
                                        borderBottom="1px solid"
                                        borderColor="anchor.gray.200"
                                        padding="1rem 1.5rem"
                                        w="100%"
                                        boxSizing="border-box"
                                    >
                                        <ClaimRow claim={claim} />
                                    </List.Item>
                                ))}

                                {isLoading && (
                                    <List.Item p={4} textAlign="center">
                                        <Text>Loading claims...</Text>
                                    </List.Item>
                                )}
                            </>
                        )}
                    </List.Root>
                </Flex>
            </Box>
        </Flex>
    );
};

const ClaimRow = ({ claim }: { claim: ClaimsItemDto }) => (
    <Box w="100%">
        <Flex gap="0.25rem" flexWrap="wrap">
            <Text fontSize="14px" asChild>
                <b>
                    <TruncatedText text={claim.id ?? ""} />
                </b>
            </Text>
            {" - "}
            <Text fontSize="14px" maxW="6rem" asChild>
                <b>
                    <TruncatedText text={claim.description ?? ""} />
                </b>
            </Text>
            {" - "}
            <Text fontSize="14px" asChild>
                <b>{claim.incidentDate ?? ""}</b>
            </Text>
        </Flex>
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" fontSize="10px" mt="0.5rem">
            {[claim.vesselName?.toUpperCase(), claim.clientName?.toUpperCase()].filter(Boolean).join(" - ")}
        </Text>
    </Box>
);
